import React, { useState } from 'react';
import { useSite } from '../useSite';
import { Seo } from '../components/Seo';
import { ButtonLink, Container } from '../components/Primitives';
import { CtaBand, PageHero } from '../components/Sections';
import plans from '../content/plans';

const List = ({ title, items, testId }) =>
  items?.length ? (
    <div data-testid={testId}>
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cafe-noir">{title}</h3>
      <ul className="mt-3 text-[14px] leading-relaxed text-cafe-noir/85">
        {items.map((x) => <li key={x} className="border-t border-bone py-2.5">{x}</li>)}
      </ul>
    </div>
  ) : null;

const PlanCard = ({ plan, open, onToggle }) => {
  const { t } = useSite();
  const L = t.plansPage;
  return (
    <article className={`flex h-full flex-col border bg-white p-7 transition-[box-shadow,border-color] duration-300 md:p-8 ${open ? 'border-kombu-green shadow-[0_24px_48px_-28px_rgba(53,64,36,0.45)]' : 'border-tan/60'}`} data-testid={`plan-${plan.id}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-moss-green">{plan.subtitle}</p>
      <h2 className="mt-3 font-serif text-[2rem] leading-none text-kombu-green">{plan.name}</h2>
      <p className="mt-2 font-serif text-lg italic text-cafe-noir/80">{L.labels.frequency}: {L.frequency[plan.id]}</p>
      <p className="mt-4 flex-1 text-[14px] leading-relaxed text-cafe-noir/85">{plan.description}</p>
      <button
        type="button" onClick={onToggle} aria-expanded={open} aria-controls={`plan-panel-${plan.id}`} data-testid={`plan-toggle-${plan.id}`}
        className={`mt-6 inline-flex items-center justify-between gap-3 border px-5 py-3 text-sm font-semibold transition-colors ${open ? 'border-kombu-green bg-kombu-green text-bone-light' : 'border-kombu-green/35 text-kombu-green hover:border-kombu-green hover:bg-kombu-green/5'}`}
      >
        {open ? L.close : L.view}
        <span aria-hidden="true" className={`transition-transform duration-300 ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
    </article>
  );
};

const PlanPanel = ({ plan, open }) => {
  const { t, p } = useSite();
  const L = t.plansPage;
  return (
    <div
      id={`plan-panel-${plan.id}`} role="region" aria-label={plan.name} hidden={!open}
      className="border border-kombu-green/40 bg-white p-7 animate-in fade-in slide-in-from-top-2 duration-500 md:col-span-2 md:order-last md:p-10 xl:col-span-4"
      data-testid={`plan-details-${plan.id}`}
    >
      <div className="grid gap-10 lg:grid-cols-3 lg:gap-12">
        <div className="flex flex-col gap-8">
          <div className="hidden md:block">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-moss-green">{plan.subtitle}</p>
            <p className="mt-3 font-serif text-4xl leading-none text-kombu-green">{plan.name}</p>
            <p className="mt-2 font-serif text-lg italic text-cafe-noir/80">{L.labels.frequency}: {L.frequency[plan.id]}</p>
          </div>
          <List title={L.labels.idealFor} items={plan.idealFor} />
          <p className="border-l-2 border-moss-green bg-bone-light px-3 py-2 text-[13px] text-cafe-noir/85" data-testid={`plan-diff-${plan.id}`}>{L.diff[plan.id]}</p>
          <ButtonLink to={`${p('book')}?plan=${plan.id}`} testId={`plan-cta-${plan.id}`} className="w-full lg:w-auto">{L.cta}</ButtonLink>
        </div>
        <List title={L.labels.bookkeeping} items={plan.bookkeepingServices} />
        <div className="flex flex-col gap-8">
          <List title={L.labels.tax} items={plan.taxServices} />
          <List title={L.labels.meetings} items={plan.meetings} />
        </div>
      </div>
    </div>
  );
};

export default function PlansPage() {
  const { t, lang } = useSite();
  const L = t.plansPage;
  const [openId, setOpenId] = useState(null);
  return (
    <>
      <Seo title={t.seo.plans.title} description={t.seo.plans.description} />
      <PageHero overline={L.overline} title={L.h1} intro={L.intro} photo="advisory-desk" />
      <section className="bg-bone-light py-16 md:py-24">
        <Container className="max-w-7xl">
          <div className="grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="plans-grid">
            {plans[lang].map((plan) => (
              <React.Fragment key={plan.id}>
                <PlanCard plan={plan} open={openId === plan.id} onToggle={() => setOpenId(openId === plan.id ? null : plan.id)} />
                <PlanPanel plan={plan} open={openId === plan.id} />
              </React.Fragment>
            ))}
          </div>
          <div className="mt-16" data-testid="plans-summary">
            <h2 className="font-serif text-3xl text-kombu-green">{L.summaryTitle}</h2>
            <div className="mt-8 grid gap-10 md:grid-cols-3">
              {L.summary.map((x) => (
                <div key={x.t} className="border-t border-tan pt-5">
                  <h3 className="font-serif text-xl text-kombu-green">{x.t}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-cafe-noir/80">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-8 max-w-3xl text-sm leading-relaxed text-cafe-noir/70" data-testid="plans-note">{L.note}</p>
        </Container>
      </section>
      <CtaBand />
    </>
  );
}
